function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/`;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

function reportInputValidity(input, errorBlock) {
  if (!input || !errorBlock) return false;

  const type = input.type;
  const value = input.value.trim();
  let isValid = true;
  let errorType = "empty";

  isValid = value === "" ? false : true;

  switch (type) {
    case "email":
      if (value !== "" && !input.checkValidity()) {        
        isValid = false;
        errorType = "email"
      }
      break;
  }

  if (isValid) {
    input.removeAttribute("aria-invalid");
    errorBlock.innerText = "";
  } else {
    input.setAttribute("aria-invalid", true);
    errorBlock.innerText = window.errorStrings[errorType];
  }

  return isValid;
}

function togglePageScroll() {
  if (document.documentElement.style.overflow == "hidden") {
    document.documentElement.style.overflow = null;
  } else {
    document.documentElement.style.overflow = "hidden";
  }
}

if (Shopify.designMode) {
  document.documentElement.classList.add('shopify-design-mode');
}

/* Video autoplay on scroll */
document.querySelectorAll('[data-video-autoplay]').forEach(container => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      container.querySelectorAll('video').forEach(video => {
        entry.isIntersecting ? video.play() : video.pause();
      });
    });
  }, { threshold: 0.3 });
  observer.observe(container);
});

// --- SECTIONS --- //

class Header {
  constructor(root) {
    this.root = root;
    this.mobileMenu = this.root.querySelector("#HeaderMobileMenu");
    this.headerSearch = this.root.querySelector("#HeaderSearch");
    this.headerPredictiveResults = this.root.querySelector("#HeaderPredictiveResults");
    this.allAnnouncementsHeight = 0;
    this.menuOpen = false;

    this.root.addEventListener('click', this.onClick.bind(this));
    this.initMegamenu();

    this.calcHeader();
    window.addEventListener('scroll', () => {
      this.root.classList.toggle('sticky', window.scrollY >= this.allAnnouncementsHeight);
    });

    if (this.headerSearch) {
      this.headerSearch.querySelector("input").addEventListener('input', debounce((e) => {
        this.onPredictiveChange(e.target.value);
      }, 500).bind(this));
      this.headerSearch.querySelector("form").addEventListener('submit', (e) => {
        e.preventDefault();
        this.onPredictiveChange(this.headerSearch.querySelector("input").value);
      });
    }
  }

  onPredictiveChange(query) {
    if (this.isLoading) return;

    if (query === "") {
      this.headerPredictiveResults.innerHTML = "";
      return;
    }

    this.isLoading = !this.isLoading;

    const searchUrl = `/search/suggest?q=${encodeURIComponent(query.trim())}&resources[type]=product&section_id=predictive-results`;

    fetch(searchUrl)
    .then(response => {
      if (!response.ok) {
        this.headerPredictiveResults.innerHTML = "";
        return;
      }

      return response.text();
    })
    .then(response => {
      const responseMarkup = new DOMParser().parseFromString(response, 'text/html');
      this.headerPredictiveResults.innerHTML = responseMarkup.querySelector("#shopify-section-predictive-results").innerHTML;
    })
    .catch(error => console.error(error))
    .finally(() => this.isLoading = !this.isLoading)
  }

  calcHeader() {
		const headerObserver = new ResizeObserver(() => {
			this.headerHeight = this.root.getBoundingClientRect().height;

      const announcements = document.querySelectorAll(".announcement");
      this.allAnnouncementsHeight = Math.max(Array.from(announcements).reduce((sum, el) => {
        return sum + el.offsetHeight;
      }, 0), 0.3);
      
      document.documentElement.style.setProperty('--menu-height', `${this.headerHeight + this.allAnnouncementsHeight}px`);
      document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);
      this.root.classList.toggle('sticky', window.scrollY >= this.allAnnouncementsHeight);
		});

		headerObserver.observe(this.root);
	}

  onClick(event) {
    if (!event.target.hasAttribute("data-action")) return;

    switch (event.target.dataset.action) {
      case "search-open":
        this.headerSearch.classList.add('open');
        document.documentElement.style.overflow = "hidden";
        break;
      case "search-close":
        this.headerSearch.classList.remove('open');
        document.documentElement.style.overflow = null;
        break;
      case "reset-search":
        this.headerSearch.querySelector("input").value = "";
        this.onPredictiveChange("");
        break;
      case "menu-toggle":
        this.toggleMobileMenu();
        break;
      case "submenu-toggle":
        const sub = event.target.nextElementSibling;

        event.target.classList.toggle("open");
        sub.style.maxHeight = event.target.classList.contains("open") ?  sub.scrollHeight + "px" : null;        
        break;
    }
  }

  initMegamenu() {
    this.root.querySelectorAll('[data-megamenu-toggle]').forEach(toggle => {
      toggle.addEventListener('mouseenter', () => {
        const id = toggle.dataset.megamenuToggle;
        const megamenu = toggle.closest('.header__megamenu-content');
        if (!megamenu) return;

        megamenu.querySelectorAll('[data-megamenu-toggle].active').forEach(el => el.classList.remove('active'));
        megamenu.querySelectorAll('[data-megamenu-item].active').forEach(el => el.classList.remove('active'));

        toggle.classList.add('active');
        const item = megamenu.querySelector(`[data-megamenu-item="${id}"]`);
        if (item) item.classList.add('active');
      });
    });
  }

  toggleMobileMenu() {
    const isOpen = !this.mobileMenu.classList.contains("open");
    this.mobileMenu.classList.toggle("open", isOpen);
    this.root.classList.toggle("menu-open", isOpen);
    document.documentElement.style.overflow = isOpen ? "hidden" : null;
  }
}

if (document.querySelector("header")) {
  new Header(document.querySelector("header"))
}

class FeaturedProducts extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this.dataset.type == "recommendations") {
      this.setRecommendationProducts();
    }
  }

  setRecommendationProducts() {
    const recommendationLink = window.Shopify.routes.root + `recommendations/products?limit=10&product_id=${this.dataset.product}&intent=${this.dataset.recommendations}&section_id=${this.dataset.section}`;
      fetch(recommendationLink)
      .then(response => response.text())
      .then(response => {
        const html = new DOMParser().parseFromString(response, "text/html");

        if (html.querySelector("featured-products[data-empty]")) {
          this.style.display = "none";
          return;
        }
        
        const grid = this.querySelector('[id^="Slider-"]');
        grid.innerHTML = html.querySelector('[id^="Slider-"]').innerHTML;

        this.querySelector("slider-component").update();
      })
      .catch(error => console.error(error))
  }
}

if (document.querySelector("featured-products")) {
  customElements.define("featured-products", FeaturedProducts);
}

class MainProduct extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.cart = document.querySelector("cart-drawer");
  }
  
  connectedCallback() {
    this.productVariants = JSON.parse(this.querySelector("[data-variants-data]").textContent);
    this.productGallery = this.querySelector('[id^="ProductGallery-"]');
    this.productOptions = this.querySelector('[id^="ProductOptions-"]');
    this.productForm = this.querySelector('[id^="ProductForm-"]');
    
    this.productGallery?.addEventListener('slider-updated', this.updateGallery.bind(this));
    this.productOptions?.addEventListener("change", this.onVariantChange.bind(this));
    this.productForm?.addEventListener("submit", this.onFormSubmit.bind(this));
    this.initClickListeners();
  }

  onFormSubmit(event = false) {
    if (event) event.preventDefault();

    if (this.isLoading) return;

    const sections = [];
    if (this.cart) sections.push("cart-drawer");

    const cartData = {
      items: [
        {
          id: parseInt(this.querySelector("[name='id']").value),
          quantity: parseInt(this.querySelector("[name='quantity']").value)
        }
      ],
      sections: sections,
      sections_url: window.location.pathname
    };

    this.addToCart(cartData);
  }

  addToCart(data) {
    if (!data) return;

    this.toggleLoading();

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    .then(response => response.json())
    .then(response => {
      if (!response.items) {
        const error = new Error(response.description);
        throw error;
      } else {
        this.toggleLoading();
        
        if (this.cart) {
          this.cart.renderContent(response.sections);
          this.cart.open();
        }
      }
    })
    .catch(error => this.showError(error))
    .finally(() => this.toggleLoading(true));
  }

  onVariantChange() {
    if (this.isLoading) return;

    this.toggleLoading();

    const searchParams = new URLSearchParams();
    let selectedValuesLabel = '';

    [1, 2, 3].forEach(option => {
      if (this.querySelector(`[data-option="${option}"]`)) {
        if (this.querySelector(`[data-option="${option}"] input:checked`)) {
          selectedValuesLabel += ' / ' + this.querySelector(`[data-option="${option}"] input:checked`).value;
        } else {
          selectedValuesLabel += ' / ' + this.querySelector(`[data-option="${option}"] input`).value;
        }
      }
    });

    this.productVariants.forEach(variant => {
      if (variant.title === selectedValuesLabel.slice(3)) {
        if (this.querySelector("input[name='id']")) {
          this.querySelector("input[name='id']").value = variant.id;
        }
        searchParams.set('variant', variant.id);
      }
    })

    const newProductUrl = window.location.origin + window.location.pathname + `?${searchParams.toString()}`;
    window.history.replaceState({}, '', newProductUrl);

    fetch(newProductUrl)
    .then(res => res.text())
    .then(body => {
      const html = new DOMParser().parseFromString(body, "text/html");

      this.renderContent(html);
    })
    .catch(error => this.showError(error))
    .finally(() => this.toggleLoading())
  }

  renderContent(html) {
    const section = html.querySelector(`[data-section="${this.dataset.section}"]`);
    const selectors = [
      '[id^="ProductPrice-"]',
      '[id^="ProductOptions-"]',
      '[id^="ProductSubmit-"]'
    ];

    selectors.forEach(selector => {
      const currentSelectors = this.querySelectorAll(selector);
      const newSelectors = section.querySelectorAll(selector);

      if (currentSelectors.length > 0 && currentSelectors.length == newSelectors.length) {
        currentSelectors.forEach((currentSelector, i) => {
          currentSelector.innerHTML = newSelectors[i].innerHTML;
        })
      }
    });

    if (this.querySelector('[id^="ProductSubmit-"]')) {
      const currentButton = this.querySelector('[id^="ProductSubmit-"]');
      const newButton = section.querySelector('[id^="ProductSubmit-"]');
      currentButton.toggleAttribute("disabled", newButton.disabled);
    }
  }

  initClickListeners() {
    this.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-action")) {
        const productGalleryComponent = this.productGallery.querySelector("slider-component");

        switch (e.target.dataset.action) {
          case "select-slide":
            productGalleryComponent.scrollToSlide(e.target.dataset.index);
            break;
          case "toggle-disclosure":
            const disclosureHandle = e.target.dataset.disclosure;
            const disclosureContent = this.querySelector(`[data-disclosure-content="${disclosureHandle}"]`);
              
            e.target.classList.toggle("open");

            if (e.target.classList.contains("open")) {
              disclosureContent.style.maxHeight = disclosureContent.scrollHeight + "px";
            } else {
              disclosureContent.style.maxHeight = null;
            }
            break;
        }
      }
    })
  }

  updateGallery(event) {    
    this.querySelectorAll('[id^="Slider-GalleryNav-"] li').forEach(slide => {
      const index = parseInt(slide.dataset.index);
      slide.classList.toggle("active", event.detail.currentSlide === index);
      if (event.detail.currentSlide === index) {
        const list = slide.parentElement;
        const offset = slide.offsetTop - list.offsetTop - (list.clientHeight / 2) + (slide.clientHeight / 2);
        list.scrollTo({ top: offset, behavior: 'smooth' });
      }
    })
  }

  showError(error) {
    console.error(error);

    if (!this.querySelector("[product-notification]")) return;

    const messageBlock = this.querySelector("[product-notification]");

    messageBlock.innerText = error.message;
    setTimeout(() => {messageBlock.innerText = ''}, 5000);
    messageBlock.scrollIntoView({behavior: "smooth",block:"center"});
  }

  toggleLoading(turnOff = false) {
    this.isLoading = !this.isLoading;
    if (turnOff) this.isLoading = false;
    this.classList.toggle("loading", this.isLoading);
  }
}

if (document.querySelector("main-product")) {
  customElements.define("main-product", MainProduct);
}

class MainCollection extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.productsGrid = this.querySelector('[id^="CollectionGrid-"]');

    this.initClickEvents();
    this.initInfiniteScroll();
  }

  initInfiniteScroll() {
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !this.isLoading) {
        const loadingInfo = this.querySelector("[data-loading] span");
        const currentPage = parseInt(loadingInfo.dataset.current);
        const totalPages = parseInt(loadingInfo.dataset.total);

        if (currentPage < totalPages) {
          this.filtersUpdate(false, (currentPage + 1));
        }
      }
    }, { rootMargin: '50px' });

    this.observer.observe(this.querySelector("[data-loading]"));
  }

  initClickEvents() {
    this.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-action")) {
        switch (e.target.dataset.action) {
          case "switch-collection":
            this.querySelector('[data-featured] .active').classList.remove("active");
            e.target.classList.add("active");
            this.filtersUpdate(e.target.dataset.collection);
            break;
          case "open-options":
            this.querySelector("[data-sort]").classList.add("open");
            break;
          case "select-option":
            this.querySelector("[name='sort_by']").value = e.target.dataset.option;
            this.querySelector("[data-sort]").classList.remove("open");
            this.filtersUpdate();
            break;
        }
      }
    })

    window.addEventListener("click", (e) => {
      if (!e.target.closest("[data-sort]")) {
        this.querySelector("[data-sort]").classList.remove("open");
      }
    })
  }

  filtersUpdate(handle = false, page = 1) {
    this.toggleLoading();
    
    let newProductUrl = "";

    if (!newProductUrl) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("sort_by", this.querySelector("[name='sort_by']").value);
      if (handle) {
        searchParams.set("page", 1);
        newProductUrl = "/collections" + `/${handle}?${searchParams.toString()}`;
      } else {
        searchParams.set("page", page);
        newProductUrl = window.location.pathname + `?${searchParams.toString()}`;
      }
    }
    
    window.history.replaceState({}, '', newProductUrl);

    fetch(newProductUrl)
    .then(res => res.text())
    .then(body => {
      const html = new DOMParser().parseFromString(body, "text/html");
      this.renderContent(html, page > 1);
    })
    .catch(error => console.error(error))
    .finally(() => this.toggleLoading())
  }

  renderContent(html, skipGrid = false) {
    const section = html.querySelector(`[data-section="${this.dataset.section}"]`);
    const selectors = [
      "[data-title]",
      "[data-description]",
      "[data-sort]",
      "[data-grid]",
      "[data-loading]"
    ];

    selectors.forEach(selector => {
      if (this.querySelector(selector) && section.querySelector(selector)) {
        if (skipGrid && selector == "[data-grid]") {
          const grid = this.querySelector(selector);
          const newItems = section.querySelectorAll(".main-collection__card");
          newItems.forEach(newItem => {
            grid.append(newItem);
          })
        } else {
          this.querySelectorAll(selector).forEach((selectorElement, i) => {
            selectorElement.innerHTML = section.querySelectorAll(selector)[i].innerHTML;
          })
        }
      }
    })
  }

  toggleLoading() {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading", this.isLoading);
  }
}

if (document.querySelector("main-collection")) {
  customElements.define("main-collection", MainCollection);
}

// --- COMPONENTS --- //

class SliderComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.currentSlide = 1;
    this.allowEvents = this.getAttribute("data-allow-events") === "true";
    this.hideControls = this.getAttribute("data-hide-controls") !== "false"

    this.slider = this.querySelector('[id^="Slider-"]');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.scrollbar = this.querySelector("[slider-scrollbar]");
    this.scrollbarThumb = this.querySelector("[slider-scrollbar] span");

    this.pagination = this.querySelector('[slider-pagination]');

    this.counter = this.querySelector('[slider-counter]');

    if (!this.slider) return;

    this.update();
    window.addEventListener('resize', this.update.bind(this));
    this.slider.addEventListener('scroll', this.update.bind(this));

    if (this.prevButton && this.nextButton) {
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    if (this.pagination) {
      this.pagination.addEventListener("click", this.onDotClick.bind(this))
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    if (event.currentTarget.name === 'previous' && this.currentSlide > 1) {
      this.currentSlide -= 1;
    } else if (event.currentTarget.name === 'next' && this.currentSlide < this.maxLength) {
      this.currentSlide += 1;
    }
    this.scrollToSlide(this.currentSlide);
  }

  onDotClick(event) {
    event.preventDefault();
    if (!event.target.hasAttribute("data-slide")) return;

    const slideIndex = parseInt(event.target.dataset.slide);
    this.scrollToSlide(slideIndex);
  }

  scrollToSlide(index) {
    this.slider.scrollTo({
      left: this.querySelector(`[id^="Slide-"]:nth-child(${index})`).offsetLeft,
      behavior: 'smooth'
    });
  }

  update() {
    const items = this.querySelectorAll('[id^="Slide-"]');
    const scrollLeft = this.slider.scrollLeft;
    const scrollWidth = this.slider.scrollWidth;
    const clientWidth = this.slider.clientWidth;
    const blockWidth = items[0]?.offsetWidth ?? 0;
    const gap = window.innerWidth < 750 ? 12 : 16;
    const hasScroll = this.slider.scrollWidth > clientWidth;
      
    this.maxLength = items.length - (Math.floor(clientWidth / blockWidth) - 1);
    this.currentSlide = Math.round(scrollLeft / (blockWidth + gap)) + 1;

    items.forEach((item, index) => item.classList.toggle("active", index + 1 == this.currentSlide));
    
    if (this.prevButton && this.nextButton) {
      this.prevButton.toggleAttribute("disabled", this.currentSlide == 1);
      this.nextButton.toggleAttribute("disabled", this.currentSlide == this.maxLength);
  
      if (this.hideControls) {
        this.prevButton.classList.toggle("hidden", !hasScroll);
        this.nextButton.classList.toggle("hidden", !hasScroll);
      }
    }

    if (this.scrollbarThumb) {      
      const thumbWidth = (clientWidth / scrollWidth) * 100;
      const thumbPosition = (scrollLeft / (scrollWidth - clientWidth)) * 100;

      this.scrollbarThumb.style.left = `${thumbPosition}%`;
      this.scrollbarThumb.style.width = `${thumbWidth}%`;
      this.scrollbarThumb.style.transform = `translateX(-${thumbPosition}%)`;
      this.scrollbar.classList.toggle("hidden", !hasScroll);
    }

    if (this.pagination) {
      this.updatePagination();
    }

    if (this.counter) {
      this.counter.innerHTML = `${this.currentSlide} / ${items.length}`;
    }
  
    if (this.allowEvents) {
      this.dispatchEvent(
        new CustomEvent("slider-updated", {
          detail: {
            currentSlide: this.currentSlide
          },
          bubbles: true,
        })
      );
    }
  }

  updatePagination() {
    const slides = this.querySelectorAll('[id^="Slide-"]');

    if (!this.slider || slides.length === 0 || !this.pagination) return;

    const containerWidth = this.slider.clientWidth;
    const slideWidth = slides[0].offsetWidth;

    const slidesPerPage = Math.floor(containerWidth / slideWidth);
    const totalPages = slides.length - (slidesPerPage - 1);

    this.pagination.innerHTML = '';
    this.pagination.classList.toggle("hidden", totalPages === 1);

    for (let i = 1; i <= totalPages; i++) {
      const dot = document.createElement('span');
      dot.setAttribute("data-slide", i);
      if (i === this.currentSlide) dot.classList.add("active");
      this.pagination.appendChild(dot);
    }
  }
}

customElements.define("slider-component", SliderComponent);

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.cart = document.querySelector("cart-drawer");
    this.quickAddButton = this.querySelector('[name="quick-add"]');
    this.isLoading = false;
  }

  connectedCallback() {
    if (this.quickAddButton) {
      this.quickAddButton.addEventListener("click", this.addToCart.bind(this));
    }
  }

  addToCart(event) {
    event.preventDefault();

    if (this.isLoading) return;

    this.toggleLoading();

    const sections = [];
    if (this.cart) sections.push("cart-drawer");

    const cartData = {
      items: [
        {
          id: parseInt(this.quickAddButton.dataset.variant),
          quantity: 1
        }
      ],
      sections: sections,
      sections_url: window.location.pathname
    };

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cartData),
    })
    .then(response => response.json())
    .then(response => {
      if (!response.items) {
        throw new Error(response.description);
      } else {
        if (this.cart) {
          this.cart.renderContent(response.sections);
          this.cart.open();
        }
      }
    })
    .catch(error => console.error(error))
    .finally(() => this.toggleLoading());
  }

  toggleLoading() {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading", this.isLoading);
  }
}

customElements.define("product-card", ProductCard);

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;

    this.initClickEvents();
  }

  connectedCallback() {
    this.initClickEvents();

    document.addEventListener("click", (e) => {
      if (e.target.hasAttribute("cart-drawer-toggle")) {
        this.open();
      }
    })
  }

  updateQuantity(item, quantity, itemEl = false) {
    this.toggleLoading(itemEl);
    
    const data = {
      line: item,
      quantity: quantity,
      sections: ["cart-drawer"],
      sections_url: window.location.pathname
    }

    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
    .then(response => response.json())
    .then(response => {
      if (response.sections) {
        this.renderContent(response.sections);
      } else {
        throw new Error(response.description);
      }
    })
    .catch((error) => {
      const itemEl = this.querySelector(`[data-item="${item}"]`);
      const messageBlock = itemEl.querySelector("[item-notification]");

      console.error('Error:', error);

      messageBlock.innerText = error.message;
      setTimeout(() => {messageBlock.innerText = ""}, 5000);
    })
    .finally(() => {this.toggleLoading(itemEl)});
  }

  renderContent(sections) {
    if (!sections['cart-drawer']) return;

    const html = new DOMParser().parseFromString(sections['cart-drawer'], "text/html");
    const newCartDrawer = html.querySelector("cart-drawer");
    
    this.querySelector("[js-content-items]").innerHTML = newCartDrawer.querySelector("[js-content-items]").innerHTML;
    this.querySelector("[js-content-footer]").innerHTML = newCartDrawer.querySelector("[js-content-footer]").innerHTML;
    this.setAttribute("data-count", newCartDrawer.dataset.count);

    document.querySelectorAll("[cart-item-count]").forEach(countEl => countEl.innerText = newCartDrawer.dataset.count);
  }

  initClickEvents() {
    this.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-action")) {
        e.preventDefault();
      }

      const item = e.target.closest("[data-item]");
      const itemQuantityInput = item?.querySelector('[name="quantity"]') ?? false;
      const itemQuantity = parseInt(itemQuantityInput?.value) ?? false;

      switch (e.target.dataset.action) {
        case "close":
          this.close();
          break;
        case "decrease-quantity":          
          if (itemQuantity > 0) {
            if (!this.isLoading) {
              this.updateQuantity(parseInt(item.dataset.item), parseInt(itemQuantity - 1), item);
            }
          }
          break;
        case "increase-quantity":
          if (itemQuantity < 199) {
            if (!this.isLoading) {
              this.updateQuantity(parseInt(item.dataset.item), parseInt(itemQuantity + 1), item);
            }
          }
          break;
        case "to-checkout":
          window.location.pathname = "/checkout";
          break;
      }
    })
  }

  toggleLoading(itemEl) {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading-item", this.isLoading);

    if (itemEl) {
      itemEl.classList.toggle("loading", this.isLoading);
    }
  }

  open() {
    this.classList.add("open");
    document.documentElement.style.overflow = "hidden";
  }

  close() {
    this.classList.remove("open");
    document.documentElement.style.overflow = null;
  }
}

if (document.querySelector("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}
